import { faCoin } from '@fortawesome/pro-light-svg-icons'
import { AlertTitle, Box, Grid, useTheme } from '@mui/material'
import React, { memo } from 'react'
import { Trans } from 'react-i18next'

import { QB_INPUT_EMAIL } from '../../../constants/input'
import { MEMBER_POINTS_TRANSACTION_TYPES } from '../../../constants/members'
import { REWARD_MEMBER_DISPATCH } from '../../../store/reducers/members/rewardMember'
import { buildSampleRewardCsv } from '../../../util/analytics/analytics.helpers'
import { buildMemberTransactionTypeIconColor } from '../../../util/transaction/transaction.helpers'
import { transWithArgs } from '../../../util/validation.helpers'
import CustomDialog from '../../shared/CustomDialog/CustomDialog'
import FaIcon from '../../shared/FaIcon/FaIcon'
import AmountOfPoints from '../../shared/inputs/AmountOfPoints'
import FileField from '../../shared/inputs/FileField'
import Input from '../../shared/inputs/Input'
import Link from '../../shared/link/Link'
import SwitchControl from '../../shared/switchControl/SwitchControl'
import CustomAlert from '../../shared/CustomAlert/CustomAlert'
import Text from '../../shared/text/Text'
import { isStringifiedJSON } from '../../../util/type.helpers'
import { parseJson } from '../../../util/app/app.helpers'

const RewardMember = ({
  t,
  onClose,
  onSubmit,
  dispatchAction,
  rewardMemberProfile: {
    isUpload,
    amount,
    account,
    isProcessing,
    error,
    bulkRewardProcessing
  },
  gridSize = 6,
  accountLabel = 'reward.member-account',
  loyaltyPoint,
  amountProps,
  accountProps,
  ...restProps
}) => {
  const theme = useTheme()
  const { icon, color } = buildMemberTransactionTypeIconColor(
    MEMBER_POINTS_TRANSACTION_TYPES.CREDIT_POINTS
  )
  const isLightIntegration = Boolean(loyaltyPoint)
  const sampleCsvUrl = buildSampleRewardCsv(isLightIntegration)
  return (
    <CustomDialog
      isForm
      title={t('reward.reward-member')}
      description={t('reward.reward-member-desc')}
      open
      onClose={onClose}
      submitProps={{
        label: t('member.transaction-filters.credit_points'),
        disabled: isUpload ? bulkRewardProcessing : isProcessing,
        startIcon: <FaIcon icon={faCoin} />
      }}
      body={
        <>
          <SwitchControl
            checked={isUpload}
            onChange={(checked) =>
              dispatchAction(REWARD_MEMBER_DISPATCH.IS_UPLOAD, checked)
            }
            leftLabel={t('reward.manually-credit-points')}
            rightLabel={t('reward.csv-upload')}
          />
          <Grid container spacing={2}>
            {loyaltyPoint}
            {isUpload ? (
              <Grid item sm={isLightIntegration ? 8 : 12} xs={12}>
                <FileField
                  id={REWARD_MEMBER_DISPATCH.REWARD_CONTENT_FILE}
                  accept='.csv, text/csv'
                  label={t('nft.upload-csv-file')}
                  errorMessage={transWithArgs(
                    t,
                    error[REWARD_MEMBER_DISPATCH.REWARD_CONTENT_FILE]
                  )}
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      dispatchAction(
                        REWARD_MEMBER_DISPATCH.REWARD_CONTENT_FILE,
                        e.target.files[0]
                      )
                    }
                  }}
                  desc={
                    <Trans
                      i18nKey={`reward.upload-csv-${
                        isLightIntegration ? 'light' : 'full'
                      }`}
                    >
                      <Link
                        isOutside
                        label={t('reward.download-example')}
                        to={sampleCsvUrl}
                        download
                      />
                    </Trans>
                  }
                  bottomLabel={
                    <Trans i18nKey='nft.download-sample-csv-here'>
                      <Link
                        isOutside
                        label={t('common.here')}
                        to={sampleCsvUrl}
                        download
                      />
                    </Trans>
                  }
                />
              </Grid>
            ) : (
              <>
                <Grid item md={gridSize} sm={gridSize} xs={12}>
                  <AmountOfPoints
                    t={t}
                    id={REWARD_MEMBER_DISPATCH.AMOUNT}
                    value={amount}
                    error={error}
                    dispatchAction={dispatchAction}
                    icon={icon}
                    color={theme.palette[color].main}
                    {...amountProps}
                  />
                </Grid>
                <Grid item md={gridSize} sm={gridSize} xs={12}>
                  <Input
                    id={REWARD_MEMBER_DISPATCH.ACCOUNT}
                    label={t(accountLabel)}
                    type={QB_INPUT_EMAIL}
                    value={account}
                    size='small'
                    onChange={(value) =>
                      dispatchAction(REWARD_MEMBER_DISPATCH.ACCOUNT, value)
                    }
                    errorMessage={transWithArgs(
                      t,
                      error[REWARD_MEMBER_DISPATCH.ACCOUNT]
                    )}
                    {...accountProps}
                  />
                </Grid>
              </>
            )}
          </Grid>
          {error?.bulkUpload && (
            <Box mt={2}>
              <CustomAlert severity='error'>
                <AlertTitle sx={{ color: 'error.main' }}>
                  {t('csvValidator.csv-error')}
                </AlertTitle>
                {!isStringifiedJSON(error?.bulkUpload) ? (
                  <Text
                    color='error.main'
                    label={t('error.something-went-wrong')}
                    variant='body2'
                  />
                ) : (
                  parseJson(error.bulkUpload).map(
                    ({ amount, email, error, accountNumber }, index) => (
                      <Text
                        key={index}
                        variant='body2'
                        color='error.main'
                        label={`Email: ${email}, Amount: ${amount} ${
                          accountNumber
                            ? `, Account number: ${accountNumber}`
                            : ''
                        } - ${error}`}
                      />
                    )
                  )
                )}
              </CustomAlert>
            </Box>
          )}
        </>
      }
      onSubmit={onSubmit}
      t={t}
      {...restProps}
    />
  )
}

export default memo(RewardMember)
